<template>
  <v-card flat class="pa-3 mt-2">

    <!-- ═══════════════════════════════════════
         CAMPAIGNS
    ══════════════════════════════════════════ -->
    <v-card-text>
      <v-card-title class="mt-0 pt-0">
        <span class="me-3">({{ clientCampaigns.length }}) Campaigns</span>
        <v-spacer></v-spacer>
        <v-text-field v-model="searchCampaigns" append-icon="mdi-magnify"
          label="Search" single-line hide-details />
      </v-card-title>
      <h3 class="ml-3">Total Cash Saved: {{ savings_total || 0 }} F CFA</h3>
      <v-data-table
        :headers="headers" :items="clientCampaigns" item-key="id"
        class="table-rounded" :items-per-page="10" disable-sort :search="searchCampaigns"
      >
        <template #[`item.name`]="{item}">
          <router-link :to="'/savings/clients/campaign/'+item.id">
            <div class="d-flex flex-column" style="cursor:pointer">
              <span class="d-block font-weight-semibold text--primary text-truncate">{{ item.name || '-' }}</span>
              <small v-if="item.target_date && item.target_amount">Type: <b>Date & Amount</b> at {{ item.percentage_completed }} Completion</small>
              <small v-else-if="item.target_amount">Type: <b>Amount</b> at {{ item.percentage_completed }} Completion</small>
              <small v-else-if="item.target_date">Type: <b>Date</b> at {{ item.percentage_completed }} Completion</small>
              <small v-else>Type: <b>Undefined</b> at --- Completion</small>
            </div>
          </router-link>
        </template>
        <template #[`item.saved_amount`]="{item}">{{ item.saved_amount || 0 }} F CFA</template>
        <template #[`item.status`]="{item}">
          <v-chip small :color="statusColor[item.status] || 'grey'" class="font-weight-medium">
            {{ item.status || '-' }}
          </v-chip>
        </template>
        <template #[`item.get_date_str`]="{item}">{{ item.get_date_str || '-' }}</template>
      </v-data-table>
    </v-card-text>

    <!-- ═══════════════════════════════════════
         LOANS
    ══════════════════════════════════════════ -->
    <v-card-text>
      <v-card-title class="mt-0 pt-0">
        <span class="me-3">({{ clientLoans.length }}) Loans</span>
        <v-spacer></v-spacer>
        <v-text-field v-model="searchLoans" append-icon="mdi-magnify"
          label="Search" single-line hide-details />
      </v-card-title>
      <h3 class="ml-3">Total Cash Owing: {{ owing_total || 0 }} F CFA</h3>
      <v-data-table
        :headers="headers" :items="clientLoans" item-key="id"
        class="table-rounded" :items-per-page="10" disable-sort :search="searchLoans"
      >
        <template #[`item.name`]="{item}">
          <router-link :to="'/savings/clients/loan/'+item.id">
            <div class="d-flex flex-column" style="cursor:pointer">
              <span class="d-block font-weight-semibold text--primary text-truncate">{{ item.name || '-' }}</span>
              <small v-if="item.target_date && item.target_amount">Type: <b>Date & Amount</b> at {{ item.percentage_completed }} Completion</small>
              <small v-else-if="item.target_amount">Type: <b>Amount</b> at {{ item.percentage_completed }} Completion</small>
              <small v-else-if="item.target_date">Type: <b>Date</b> at {{ item.percentage_completed }} Completion</small>
              <small v-else>Type: <b>Undefined</b> at --- Completion</small>
            </div>
          </router-link>
        </template>
        <template #[`item.saved_amount`]="{item}">
          <span v-if="item.is_legacy">{{ item.saved_amount || 0 }} F CFA (Saved)</span>
          <span v-else>{{ item.saved_amount || 0 }} F CFA (Paid)</span>
        </template>
        <template #[`item.status`]="{item}">
          <v-chip small :color="statusColor[item.status] || 'grey'" class="font-weight-medium">
            {{ item.status || '-' }}
          </v-chip>
        </template>
        <template #[`item.get_date_str`]="{item}">{{ item.get_date_str || '-' }}</template>
      </v-data-table>
    </v-card-text>

    <!-- ═══════════════════════════════════════
         WALLET
    ══════════════════════════════════════════ -->
    <v-card-text>
      <v-card-title class="mt-0 pt-0">
        <v-icon class="me-2" color="indigo">mdi-wallet</v-icon>
        <span>Wallet</span>
        <v-spacer></v-spacer>
      </v-card-title>
      <h3 class="ml-3">Current Balance: {{ walletBalance || 0 }} F CFA</h3>
      <v-data-table
        :headers="walletHeaders" :items="walletTransactions" item-key="id"
        class="table-rounded mt-3" :items-per-page="5" disable-sort
      >
        <template #[`item.transaction_type`]="{item}">
          <v-chip small :color="transactionTypeColor[item.transaction_type] || 'grey'" class="font-weight-medium">
            {{ item.transaction_type_display || item.transaction_type || '-' }}
          </v-chip>
        </template>
        <template #[`item.amount`]="{item}">
          <span :class="item.is_credit ? 'success--text' : 'error--text'" class="font-weight-bold">
            {{ item.is_credit ? '+' : '-' }}{{ item.amount || 0 }} F CFA
          </span>
        </template>
        <template #[`item.source_dest`]="{item}">
          <div>
            <div v-if="item.source">
              <small class="text--secondary">From: </small>
              <span class="font-weight-medium">{{ item.source_display || item.source }}</span>
            </div>
            <div v-if="item.destination">
              <small class="text--secondary">To: </small>
              <span class="font-weight-medium">{{ item.destination_display || item.destination }}</span>
            </div>
            <span v-if="!item.source && !item.destination" class="text--secondary">-</span>
          </div>
        </template>
        <template #[`item.status`]="{item}">
          <v-chip x-small :color="walletStatusColor[item.status] || 'grey'" class="font-weight-medium">
            {{ item.status || '-' }}
          </v-chip>
        </template>
        <template #[`item.description`]="{item}">
          <span class="text-truncate" style="max-width:200px;display:block">{{ item.description || '-' }}</span>
        </template>
        <template #[`item.created_at`]="{item}">{{ formatDate(item.created_at) }}</template>
      </v-data-table>
    </v-card-text>

    <!-- ═══════════════════════════════════════
         TOP-UP REQUESTS
    ══════════════════════════════════════════ -->
    <v-card-text>
      <v-card-title class="mt-0 pt-0">
        <v-icon class="me-2" color="primary">mdi-cellphone-arrow-down</v-icon>
        <span class="me-3">({{ saveTransactions.length }}) Top-Up Requests</span>
        <v-spacer></v-spacer>
        <v-text-field v-model="searchSaveTransactions" append-icon="mdi-magnify"
          label="Search" single-line hide-details />
      </v-card-title>
      <v-data-table
        :headers="saveTransactionHeaders" :items="saveTransactions" item-key="id"
        class="table-rounded mt-3" :items-per-page="5" disable-sort :search="searchSaveTransactions"
      >
        <template #[`item.type`]="{item}">
          <v-chip small :color="item.withdrawal ? 'warning' : 'teal'" dark class="font-weight-medium">
            <v-icon small left>
              {{ item.withdrawal ? 'mdi-arrow-up-bold-circle-outline' : 'mdi-arrow-down-bold-circle-outline' }}
            </v-icon>
            {{ item.withdrawal ? 'Withdrawal' : 'Deposit' }}
          </v-chip>
        </template>
        <template #[`item.campaign`]="{item}">
          <div v-if="item.campaign">
            <router-link :to="'/savings/clients/campaign/'+item.campaign.id">
              <span class="font-weight-medium primary--text">{{ item.campaign.name }}</span>
            </router-link>
            <div>
              <v-chip x-small :color="item.campaign.principal ? 'deep-purple' : 'blue-grey'" dark>
                {{ item.campaign.principal ? 'Principal' : 'Tier' }}
              </v-chip>
            </div>
          </div>
          <span v-else class="text--secondary">-</span>
        </template>
        <template #[`item.transaction_operator`]="{item}">
          <div class="d-flex align-center">
            <v-icon small class="me-1" color="grey">mdi-sim</v-icon>
            <span class="font-weight-medium">{{ item.transaction_operator || '-' }}</span>
          </div>
        </template>
        <template #[`item.amount`]="{item}">
          <!-- ✅ classes CSS Vuetify correctes -->
          <span class="font-weight-bold" :class="item.withdrawal ? 'warning--text' : 'teal--text'">
            {{ item.withdrawal ? '-' : '+' }}{{ item.amount || 0 }} F CFA
          </span>
        </template>
        <template #[`item.phone`]="{item}">
          <div class="d-flex align-center">
            <v-icon small class="me-1" color="grey">mdi-phone</v-icon>
            {{ item.phone || '-' }}
          </div>
        </template>
        <template #[`item.status`]="{item}">
          <v-chip x-small :color="getSaveTransactionColor(item)" dark class="font-weight-medium">
            <v-icon x-small left>{{ getSaveTransactionIcon(item) }}</v-icon>
            {{ getSaveTransactionLabel(item) }}
          </v-chip>
        </template>
        <template #[`item.time_created`]="{item}">
          <div class="d-flex align-center">
            <v-icon small class="me-1" color="grey">mdi-calendar-clock</v-icon>
            {{ item.time_created || '-' }}
          </div>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- ═══════════════════════════════════════
         PRINCIPAL CAMPAIGN
    ══════════════════════════════════════════ -->
    <v-card-text v-if="principalCampaign">
      <v-card-title class="mt-0 pt-0">
        <v-icon class="me-2" color="deep-purple">mdi-bank</v-icon>
        <span>Principal Campaign</span>
        <v-spacer></v-spacer>
        <v-btn color="deep-purple" dark small @click="dialogPrincipal = true">
          <v-icon left small>mdi-calculator</v-icon>
          View Balance Breakdown
        </v-btn>
      </v-card-title>
      <h3 class="ml-3">
        {{ principalCampaign.name || 'Principal' }} —
        Saved Amount: <span class="deep-purple--text">{{ principalCampaign.saved_amount || 0 }} F CFA</span>
      </h3>
    </v-card-text>

    <!-- ═══════════════════════════════════════
         DIALOGUE BREAKDOWN PRINCIPAL
    ══════════════════════════════════════════ -->
    <v-dialog v-model="dialogPrincipal" max-width="1050" scrollable>
      <v-card v-if="principalCampaign">

        <v-card-title class="deep-purple darken-2 white--text py-3">
          <v-icon dark class="me-2">mdi-bank</v-icon>
          {{ principalCampaign.name || 'Principal Campaign' }} — Balance Breakdown
          <v-spacer></v-spacer>
          <v-btn icon dark @click="dialogPrincipal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4 pb-0">

          <!-- ── 4 cards résumé ──────────────────────────────── -->
          <v-row dense class="mb-4">
            <v-col cols="6" sm="3">
              <v-card outlined class="text-center pa-3 rounded-lg">
                <div class="caption text--secondary mb-1">System Value</div>
                <div class="font-weight-bold deep-purple--text title">
                  {{ principalCampaign.saved_amount || 0 }} F CFA
                </div>
                <div class="caption text--secondary">saved_amount</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card outlined class="text-center pa-3 rounded-lg">
                <div class="caption text--secondary mb-1">Total Deposited</div>
                <div class="font-weight-bold teal--text title">
                  +{{ principalSummary.totalDeposits }} F CFA
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card outlined class="text-center pa-3 rounded-lg">
                <div class="caption text--secondary mb-1">Total Withdrawn</div>
                <div class="font-weight-bold warning--text title">
                  −{{ principalSummary.totalWithdrawals }} F CFA
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card color="deep-purple darken-2" dark class="text-center pa-3 rounded-lg">
                <div class="caption mb-1">Calculated Balance</div>
                <div class="font-weight-bold title">{{ principalSummary.balance }} F CFA</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- ── Alerte pending ─────────────────────────────── -->
          <v-alert
            v-if="principalSummary.pendingCount > 0"
            type="warning" dense text class="mb-4 caption"
          >
            <strong>{{ principalSummary.pendingCount }} pending request(s)</strong>
            totaling <strong>{{ principalSummary.totalPending }} F CFA</strong>
            awaiting validation — <u>not counted</u> in the balance above.
          </v-alert>

          <!-- ── Onglets ─────────────────────────────────────── -->
          <v-tabs v-model="principalTab" color="deep-purple" class="mb-3">
            <v-tab>
              <v-icon small left>mdi-format-list-bulleted</v-icon>
              All Transactions ({{ principalCampaign.transactions.length }})
            </v-tab>
            <v-tab>
              <v-icon small left>mdi-check-circle-outline</v-icon>
              Completed Campaigns ({{ principalCampaign.completed_contributions.length }})
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="principalTab">

            <!-- ── Tab 1 : toutes les transactions ───────────── -->
            <v-tab-item>
              <v-simple-table dense class="mb-2">
                <template #default>
                  <thead>
                    <tr class="deep-purple lighten-5">
                      <th style="width:36px">#</th>
                      <th>Type</th>
                      <th>Origin</th>
                      <th>Details</th>
                      <th>Date</th>
                      <th class="text-right">Amount</th>
                      <th class="text-right">Running Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(tx, index) in principalTransactionsWithRunning"
                      :key="tx.id"
                      :class="tx.pending ? 'grey lighten-4' : ''"
                    >
                      <!-- # -->
                      <td class="caption text--secondary">{{ index + 1 }}</td>

                      <!-- Type -->
                      <td>
                        <!-- ✅ Chip type opération -->
                        <v-chip x-small dark
                          :color="tx.pending ? 'amber' : tx.withdrawal ? 'warning' : 'teal'"
                        >
                          <v-icon x-small left>
                            {{ tx.pending ? 'mdi-clock-outline' : tx.withdrawal ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                          </v-icon>
                          {{ tx.pending ? 'Pending' : tx.withdrawal ? 'Withdrawal' : 'Deposit' }}
                        </v-chip>
                        <!-- ✅ Chip source -->
                        <div class="mt-1">
                          <v-chip x-small outlined
                            :color="sourceChipColor(tx.source)"
                          >
                            <v-icon x-small left>{{ sourceChipIcon(tx.source) }}</v-icon>
                            {{ sourceChipLabel(tx.source) }}
                          </v-chip>
                        </div>
                      </td>

                      <!-- Origin -->
                      <td>
                        <!-- Campagne tier complétée -->
                        <div v-if="tx.source === 'completed_campaign' && tx.source_campaign">
                          <router-link
                            :to="'/savings/clients/campaign/'+tx.source_campaign.id"
                            @click.native="dialogPrincipal = false"
                          >
                            <span class="font-weight-medium primary--text caption">
                              {{ tx.source_campaign.name }}
                            </span>
                          </router-link>
                          <div class="caption text--secondary">
                            Saved: {{ tx.source_campaign.saved_amount }} F CFA ·
                            <v-chip x-small :color="statusColor[tx.source_campaign.status] || 'grey'">
                              {{ tx.source_campaign.status || '-' }}
                            </v-chip>
                          </div>
                        </div>
                        <!-- Wallet -->
                        <div v-else-if="tx.source === 'wallet_transaction'">
                          <div class="d-flex align-center">
                            <v-icon small color="indigo" class="me-1">mdi-wallet</v-icon>
                            <span class="caption font-weight-medium indigo--text">Wallet</span>
                          </div>
                          <div v-if="tx.related_user" class="caption text--secondary">
                            <v-icon x-small>mdi-account-arrow-right</v-icon> {{ tx.related_user }}
                          </div>
                          <div v-if="tx.wallet_ref && tx.wallet_ref !== '-'" class="caption text--secondary">
                            Ref: {{ tx.wallet_ref }}
                          </div>
                        </div>
                        <!-- CampaignTransaction directe -->
                        <div v-else-if="tx.source === 'campaign_transaction'">
                          <span class="caption text--secondary">
                            {{ tx.withdrawal ? 'Admin Withdrawal' : 'Admin Deposit' }}
                          </span>
                        </div>
                        <!-- Request pending -->
                        <div v-else>
                          <span class="caption text--secondary">Mobile Request</span>
                        </div>
                      </td>

                      <!-- Details -->
                      <td>
                        <div class="d-flex align-center">
                          <v-icon x-small class="me-1" color="grey">mdi-sim</v-icon>
                          <span class="caption">{{ tx.means_of_payment || '-' }}</span>
                        </div>
                        <div v-if="tx.transactor_number && tx.transactor_number !== '-'" class="caption text--secondary">
                          <v-icon x-small>mdi-phone</v-icon> {{ tx.transactor_number }}
                        </div>
                        <div v-if="tx.top_up_admin && tx.top_up_admin !== '-'" class="caption text--secondary">
                          <v-icon x-small>mdi-account</v-icon> {{ tx.top_up_admin }}
                        </div>
                        <div v-if="tx.fee > 0" class="caption error--text">
                          Fee: −{{ tx.fee }} F CFA
                        </div>
                        <div v-if="tx.wallet_description && tx.wallet_description !== '-'"
                          class="caption text--secondary text-truncate" style="max-width:150px"
                        >
                          {{ tx.wallet_description }}
                        </div>
                      </td>

                      <!-- Date -->
                      <td>
                        <span class="caption text--secondary">{{ tx.date_time || '-' }}</span>
                      </td>

                      <!-- Amount -->
                      <td class="text-right">
                        <!-- ✅ classes CSS correctes -->
                        <span class="font-weight-bold"
                          :class="tx.withdrawal ? 'warning--text' : 'teal--text'"
                        >
                          {{ tx.withdrawal ? '−' : '+' }}{{ tx.amount || 0 }} F CFA
                        </span>
                      </td>

                      <!-- Running total -->
                      <td class="text-right">
                        <span v-if="tx.pending" class="caption text--secondary font-italic">(not counted)</span>
                        <span v-else class="font-weight-bold">{{ tx.running_total }} F CFA</span>
                      </td>
                    </tr>

                    <tr v-if="!principalCampaign.transactions || principalCampaign.transactions.length === 0">
                      <td colspan="7" class="text-center text--secondary py-6">
                        <v-icon large color="grey lighten-2">mdi-inbox-outline</v-icon>
                        <div>No transactions found.</div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-tab-item>

            <!-- ── Tab 2 : campagnes complétées ──────────────── -->
            <v-tab-item>
              <div v-if="principalCampaign.completed_contributions && principalCampaign.completed_contributions.length > 0">
                <v-list dense>
                  <v-list-item
                    v-for="camp in principalCampaign.completed_contributions"
                    :key="camp.id"
                    class="mb-1"
                  >
                    <v-list-item-icon>
                      <v-icon color="success">mdi-check-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        <router-link :to="'/savings/clients/campaign/'+camp.id" @click.native="dialogPrincipal=false">
                          <span class="font-weight-semibold primary--text">{{ camp.name }}</span>
                        </router-link>
                      </v-list-item-title>
                      <v-list-item-subtitle class="caption">
                        {{ camp.date }} ·
                        <v-chip x-small :color="statusColor[camp.status] || 'grey'" dark>
                          {{ camp.status }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <div class="text-right">
                        <div class="font-weight-bold teal--text">+{{ camp.saved_amount }} F CFA</div>
                        <div class="caption text--secondary">→ to principal</div>
                      </div>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
                <v-divider class="my-3"></v-divider>
                <div class="text-right pr-4 pb-3">
                  <span class="caption text--secondary">Total: </span>
                  <span class="font-weight-bold teal--text">
                    +{{ principalCampaign.completed_contributions.reduce((s, c) => s + c.saved_amount, 0) }} F CFA
                  </span>
                </div>
              </div>
              <div v-else class="text-center py-6 text--secondary">
                <v-icon large color="grey lighten-2">mdi-folder-open-outline</v-icon>
                <div>No completed campaigns found.</div>
              </div>
            </v-tab-item>

          </v-tabs-items>
        </v-card-text>

        <v-card-actions class="py-3">
          <v-spacer></v-spacer>
          <v-btn text color="deep-purple" @click="dialogPrincipal = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-card>
</template>
<script>
import Spinner from "@/components/Spinner.vue"
import axios from 'axios'

export default {
  components: { Spinner },

  data() {
    return {
      statusColor: {
        Ongoing: 'primary', Completed: 'success',
        Interrupted: 'error', Inactivated: 'error',
      },
      transactionTypeColor: {
        DEPOSIT: 'success', WITHDRAWAL: 'warning',
        TRANSFER_OUT: 'error', TRANSFER_IN: 'success',
      },
      walletStatusColor: {
        PENDING: 'warning', COMPLETED: 'success', FAILED: 'error',
      },

      headers: [
        { text: 'Name',         value: 'name' },
        { text: 'Amount Saved', value: 'saved_amount' },
        { text: 'Status',       value: 'status' },
        { text: 'Date/Time',    value: 'get_date_str' },
      ],
      walletHeaders: [
        { text: 'Type',               value: 'transaction_type' },
        { text: 'Amount',             value: 'amount' },
        { text: 'Source/Destination', value: 'source_dest' },
        { text: 'Status',             value: 'status' },
        { text: 'Description',        value: 'description' },
        { text: 'Date/Time',          value: 'created_at' },
      ],
      saveTransactionHeaders: [
        { text: 'Type',      value: 'type' },
        { text: 'Campaign',  value: 'campaign' },
        { text: 'Operator',  value: 'transaction_operator' },
        { text: 'Amount',    value: 'amount' },
        { text: 'Phone',     value: 'phone' },
        { text: 'Status',    value: 'status' },
        { text: 'Date/Time', value: 'time_created' },
      ],

      client_id: this.$route.params.client_id,
      loading: false,
      searchCampaigns: '',
      searchLoans: '',
      searchSaveTransactions: '',
      savings_total: '',
      owing_total: '',
      clientCampaigns: [],
      clientLoans: [],
      walletBalance: '0',
      walletTransactions: [],
      saveTransactions: [],
      principalCampaign: null,
      dialogPrincipal: false,
      principalTab: 0,
    }
  },

  computed: {
    // ── Running total ligne par ligne ──────────────────────────
    principalTransactionsWithRunning() {
      if (!this.principalCampaign?.transactions) return []
      let running = 0
      return this.principalCampaign.transactions.map(tx => {
        if (!tx.pending) {
          running = tx.withdrawal
            ? running - (tx.amount || 0)
            : running + (tx.amount || 0)
        }
        return { ...tx, running_total: running }
      })
    },

    // ── Totaux résumé ──────────────────────────────────────────
    principalSummary() {
      if (!this.principalCampaign?.transactions) {
        return { totalDeposits: 0, totalWithdrawals: 0, balance: 0, totalPending: 0, pendingCount: 0 }
      }
      let totalDeposits = 0, totalWithdrawals = 0, totalPending = 0, pendingCount = 0
      for (const tx of this.principalCampaign.transactions) {
        if (tx.pending) {
          totalPending  += tx.amount || 0
          pendingCount++
        } else if (tx.withdrawal) {
          totalWithdrawals += tx.amount || 0
        } else {
          totalDeposits    += tx.amount || 0
        }
      }
      return { totalDeposits, totalWithdrawals, balance: totalDeposits - totalWithdrawals, totalPending, pendingCount }
    },
  },

  methods: {
    // ── Helpers chip source ────────────────────────────────────
    sourceChipColor(source) {
      return {
        completed_campaign: 'success',
        wallet_transaction:  'indigo',
        request_save:        'primary',
        campaign_transaction:'blue-grey',
      }[source] || 'grey'
    },
    sourceChipIcon(source) {
      return {
        completed_campaign:  'mdi-check-circle',
        wallet_transaction:  'mdi-wallet',
        request_save:        'mdi-cellphone',
        campaign_transaction:'mdi-bank-transfer',
      }[source] || 'mdi-help'
    },
    sourceChipLabel(source) {
      return {
        completed_campaign:  'Completed Campaign',
        wallet_transaction:  'Wallet',
        request_save:        'Mobile Request',
        campaign_transaction:'Admin Tx',
      }[source] || source || '-'
    },

    // ── Fetch ──────────────────────────────────────────────────
    async getClientCampaigns() {
      this.loading = true
      await axios.get('/api/v1/manager/get/client/campaigns/' + this.client_id + '/')
        .then(r => {
          this.clientCampaigns = r.data['user_campaigns'] || []
          this.savings_total   = r.data['total_saved']    || 0
        })
        .catch(() => this.showError("An error occurred while loading campaigns"))
      this.loading = false
    },

    async getClientLoans() {
      this.loading = true
      await axios.get('/api/v1/manager/get/client/loans/' + this.client_id + '/')
        .then(r => {
          this.clientLoans = r.data['user_loans']  || []
          this.owing_total = r.data['total_owing'] || 0
        })
        .catch(() => this.showError("An error occurred while loading loans"))
      this.loading = false
    },

    async getClientWallet() {
      this.loading = true
      await axios.get('/api/v1/manager/get/client/wallet/' + this.client_id + '/')
        .then(r => {
          this.walletBalance = r.data['balance'] || 0
          this.walletTransactions = (r.data['transactions'] || []).map(tx => ({
            ...tx,
            transaction_type_display: this.formatTransactionType(tx.transaction_type),
            source_display:           this.formatSourceDest(tx.source),
            destination_display:      this.formatSourceDest(tx.destination),
            is_credit: ['DEPOSIT', 'TRANSFER_IN'].includes(tx.transaction_type),
          }))
          this.saveTransactions  = r.data['save_transactions']  || []
          this.principalCampaign = r.data['principal_campaign'] || null
        })
        .catch(() => this.showError("An error occurred while loading wallet"))
      this.loading = false
    },

    // ── Formatage ──────────────────────────────────────────────
    formatTransactionType(type) {
      return { DEPOSIT: 'Deposit', WITHDRAWAL: 'Withdrawal',
               TRANSFER_OUT: 'Transfer Out', TRANSFER_IN: 'Transfer In' }[type] || (type || '-')
    },
    formatSourceDest(value) {
      return { BUSINESS_BALANCE: 'Business Balance', MARKETER_CAMPAIGN: 'Marketer Campaign',
               PRINCIPAL_CAMPAIGN: 'Principal Campaign', WALLET: 'Wallet' }[value] || (value || '-')
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const d = new Date(dateString)
      if (isNaN(d)) return dateString
      return d.toLocaleString('en-GB', {
        weekday: 'short', day: '2-digit', month: 'short',
        year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
      })
    },

    // ── Save transactions ──────────────────────────────────────
    getSaveTransactionColor(item) {
      if (item.cancelled) return 'error'
      if (item.confirmed) return 'success'
      return 'warning'
    },
    getSaveTransactionLabel(item) {
      if (item.cancelled) return 'Cancelled'
      if (item.confirmed) return 'Confirmed'
      return 'Pending'
    },
    getSaveTransactionIcon(item) {
      if (item.cancelled) return 'mdi-close-circle-outline'
      if (item.confirmed) return 'mdi-check-circle-outline'
      return 'mdi-clock-outline'
    },

    showError(msg) {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', 'red darken-4')
      this.$store.commit('activateSnackBar', true)
    },
  },

  mounted() {
    this.getClientWallet()
    this.getClientCampaigns()
    this.getClientLoans()
  },
}
</script>
